import { useState, useEffect } from "react";
import {
  Plus,
  RefreshCcw,
  Table,
  History,
  Pencil,
  Search,
  IndianRupee,
} from "lucide-react";

import OrderVendorsAction from "./OrderVendorsAction";
import useOrderVendors from "../../../services/Order/useOrderVendors";
import usePayout from "../../../services/Order/usePayout";
import useVendors from "../../../services/Inventory/useVendors";
import LoadingScreen from "../../../utils/LoadingScreen";
import { toast, ToastContainer } from "react-toastify";
import { formatDate, formatDateTime } from "../../../utils/dateFormatter";


const VENDOR_TYPES = [
  "Retailers",
  "Service providers",
  "Manufacturer",
  "Wholesaler",
  "Wholesalers or distributors",
  "Independent craftspeople",
  "B2c vendors",
  "Independent Vendor",
  "B2b",
  "Service vendors",
  "Retail vendors",
];

const PRODUCT_CATEGORIES = [
  "Electronics",
  "Dairy",
  "Toys",
  "Grocery",
  "Fresh Produce",
  "Meat & Seafood",
  "Bakery",
  "Beverages",
  "Frozen",
  "Household",
  "Personal Care",
  "Stationery",
  "Apparel",
  "Hardware",
  "Automotive",
  "Pharmacy",
  "Beauty",
  "Baby",
  "Pet Care",
  "Other",
];

export default function OrderVendors() {
  const [activeTab, setActiveTab] = useState("vendors");
  const [search, setSearch] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [openCategory, setOpenCategory] = useState(false);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [showVendorPurchase, setShowVendorPurchase] = useState(false);

  const {
    vendors = [],
    loading,
    fetchVendors,
    addVendor,
    editVendor,
    deleteVendor,
  } = useVendors();

  const { wallet, getWalletBalance } = usePayout();

  const {
    purchaseList,
    getVendorPurchaseList,
    processVendorPayment,
    addVendorPurchase,
  } = useOrderVendors();

  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showEditVendor, setShowEditVendor] = useState(false);
  const [showAddPurchase, setShowAddPurchase] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [date, setDate] = useState(today);
  const [vendorNameInput, setVendorNameInput] = useState("");

  const [form, setForm] = useState({
    vendorName: "",
    vendorType: "",
    vendorCategory: "",
    vendorAddress: "",
    gst: "",
    mobile: "",
    state: "",
    city: "",
    accountNumber: "",
    ifscCode: "",
  });

  const resetVendorForm = () => {
    setForm({
      vendorName: "",
      vendorType: "",
      vendorCategory: "",
      vendorAddress: "",
      gst: "",
      mobile: "",
      state: "",
      city: "",
      accountNumber: "",
      ifscCode: "",
    });
  };

  const resetPurchaseForm = () => {
    setAmount("");
    setDesc("");
    setDate(today);
  };

  const openAddVendor = () => {
    resetVendorForm();
    setShowAddVendor(true);
  };

  const closeAddVendor = () => {
    setShowAddVendor(false);
  };

  const handleAddVendor = async () => {
    const payload = {
      vendorName: form.vendorName,
      vendorType: form.vendorType,
      vendorCategory: form.vendorCategory,
      vendorAddress: form.vendorAddress,
      gst: form.gst,
      mobile: form.mobile,
      state: form.state,
      city: form.city,
      accountNumber: form.accountNumber,
      ifscCode: form.ifscCode,
    };

    await addVendor(payload);

    closeAddVendor();
    // fetchVendors();
  };

  const openEditVendor = (vendor) => {
    setSelectedVendor(vendor);
    setShowEditVendor(true);
  };

  const closeEditVendor = () => {
    setSelectedVendor(null);
    setShowEditVendor(false);
  };

  const handleEditVendor = async () => {
    const payload = {
      vendorId: selectedVendor.id,
      vendorName: selectedVendor.vendorName,
      vendorType: selectedVendor.vendorType,
      vendorCategory: selectedVendor.vendorCategory,
      vendorAddress: selectedVendor.vendorAddress,
      gst: selectedVendor.gst,
      mobile: selectedVendor.mobile,
      state: selectedVendor.state,
      city: selectedVendor.city,
      accountNumber: selectedVendor.account_number,
      ifscCode: selectedVendor.ifsc,
    };

    await editVendor(payload);

    closeEditVendor();
  };

  const closeAddPurchase = () => {
    setShowAddPurchase(false);
    setSelectedVendor(null);
  };

  const handleAddPurchase = async () => {
    if (!vendorNameInput || !amount || !date || !paymentMode) {
      toast.error("Please fill all required fields");
      return;
    }

    const payload = {
      vendorName: vendorNameInput,
      amount: amount,
      purchaseDescription: desc,
      purchaseDate: date,
      paymentMode: paymentMode,
    };

    await addVendorPurchase(payload);

    closeAddPurchase();
    getVendorPurchaseList(fromDate, toDate);
  };

  const openPayModal = (purchase, mode) => {
    setSelectedPurchase(purchase);
    setPaymentMode(mode);
    setShowPayModal(true);
  };

  const closePayModal = () => {
    setSelectedPurchase(null);
    setShowPayModal(false);
  };

  const handleConfirmPayment = async () => {
    await processVendorPayment({
      vendorId: selectedPurchase.fk_vendor_id,
      purchaseId: selectedPurchase.id,
      amount: selectedPurchase.amount,
      mode: paymentMode,
    });

    closePayModal();
    getVendorPurchaseList(fromDate, toDate);
  };

  const closeVendorPurchase = () => {
    setShowVendorPurchase(false);
    setSelectedVendor(null);
  };

  const handleVendorPurchase = async () => {
    if (!selectedVendor || !amount || !date) {
      toast.error("Please fill all required fields");
      return;
    }
    await addVendorPurchase({
      vendorId: selectedVendor.id,
      amount,
      purchaseDescription: desc,
      purchaseDate: date,
      mode: "cash",
    });

    closeVendorPurchase();
    getVendorPurchaseList(fromDate, toDate);
  };

  useEffect(() => {
    fetchVendors();
    getWalletBalance();
  }, []);

  useEffect(() => {
    if (activeTab === "purchases") {
      getVendorPurchaseList();
    }
  }, [activeTab]);

  const filteredVendors = (vendors || []).filter((v) =>
    JSON.stringify(v).toLowerCase().includes(search.toLowerCase()),
  );

  const filteredPurchases = (purchaseList || []).filter((p) =>
    JSON.stringify(p).toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
    <ToastContainer/>
      <div className="p-6 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
        {/* HEADER */}
        <div className=" bg-white p-5 rounded-lg mb-6 montserrat-font-1">
          <div className="flex justify-between items-center mb-6 ">
            <h1 className="text-2xl font-semibold text-gray-800">
              Vendor Management
            </h1>

            <div className="flex items-center gap-3">
              <div className="bg-white border rounded-lg px-4 py-2 text-sm">
                Vendor Balance
                <span className="font-semibold ml-2">
                  ₹ {wallet?.wallet_bal || "0"}
                </span>
              </div>

              <button
                onClick={() => setShowAddPurchase(true)}
                className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg"
              >
                <Plus size={16} />
                Add Purchase
              </button>

              <button
                onClick={openAddVendor}
                className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg"
              >
                <Plus size={16} />
                Add Vendor
              </button>

              <button
                onClick={() => fetchVendors()}
                className="p-2 border rounded-lg bg-green-500 text-white items-center gap-2 flex"
              >
                <RefreshCcw size={16} />
                Refresh
              </button>
            </div>
          </div>

          {/* TABS */}

          <div className="border-b mb-6 bg-white p-4 rounded-lg montserrat-font-1">
            <div className="flex gap-6 text-sm font-medium montserrat-font-1">
              <button
                onClick={() => setActiveTab("vendors")}
                className={`pb-2 flex items-center gap-2 border-b-2 ${
                  activeTab === "vendors"
                    ? "border-green-600 text-green-600"
                    : "border-transparent text-gray-500"
                }`}
              >
                <Table size={16} />
                Vendors
              </button>

              <button
                onClick={() => setActiveTab("purchases")}
                className={`pb-2 flex items-center gap-2 border-b-2 ${
                  activeTab === "purchases"
                    ? "border-green-600 text-green-600"
                    : "border-transparent text-gray-500"
                }`}
              >
                <History size={16} />
                Purchase History
              </button>
            </div>
          </div>

          {/* SEARCH */}

          {activeTab === "vendors" && (
            <div className="flex justify-between mb-4 montserrat-font-1">
              <div className="flex items-center border rounded-lg px-3 py-2 w-80 bg-white">
                <Search size={16} className="text-gray-400 mr-2" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="outline-none w-full"
                />
              </div>

              <div className="bg-white border px-3 py-1 rounded-lg text-sm">
                Total: {filteredVendors.length}
              </div>
            </div>
          )}

          {activeTab === "purchases" && (
            <div className="flex justify-between items-center mb-4 montserrat-font-1">
              {/* Date Filters */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">From</span>

                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="border rounded-md px-2 py-1 text-sm"
                />

                <span className="text-sm text-gray-500">To</span>

                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="border rounded-md px-2 py-1 text-sm"
                />

                <button
                  onClick={() => getVendorPurchaseList(fromDate, toDate)}
                  className="flex items-center gap-1 bg-green-600 text-white px-4 py-1.5 rounded-md text-sm"
                >
                  Apply
                </button>
              </div>

              {/* Search */}
              <div className="flex items-center gap-2">
                <div className="flex items-center border rounded-lg px-3 py-1 bg-white">
                  <Search size={14} className="text-gray-400 mr-2" />

                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by purchase id, vendor, desc..."
                    className="outline-none text-sm w-64"
                  />
                </div>

                <span className="bg-gray-100 px-3 py-1.5 rounded-md text-xs">
                  Total: {filteredPurchases.length}
                </span>
              </div>
            </div>
          )}
        </div>
        {/* VENDORS TABLE */}

        {activeTab === "vendors" && (
          <div className="bg-white border rounded-xl overflow-auto h-full  insert-0 montserrat-font-1">
            <table className="w-full text-sm insert-0 montserrat-font-1">
               <thead className="sticky top-0 z-10 bg-green-100 text-gray-600">
                <tr>
                  <th className="p-3 text-left">Vendor</th>
                  <th className="p-3 text-left">Type / Category</th>
                  <th className="p-3 text-left">Mobile</th>
                  <th className="p-3 text-left">State</th>
                  <th className="p-3 text-left">City</th>
                  <th className="p-3 text-left">Account #</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Created On</th>
                  <th className="p-3 text-left">Add Purchase</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="py-16">
                      <div className="flex justify-center items-center w-full">
                        <LoadingScreen text="Loading tab data..." />
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredVendors.map((v) => (
                    <tr key={v.id} className="border-t hover:bg-gray-50">
                      <td className="p-3">
                        <div className="font-medium">{v.vendorName}</div>

                        <div className="text-xs text-gray-500">
                          GST: {v.gst || "N/A"}
                        </div>
                      </td>

                      <td className="p-3 flex gap-2">
                        <span className="bg-gray-100 px-2 py-1.5 rounded-full text-xs font-semibold">
                          {v.vendorType}
                        </span>

                        <span className="bg-gray-50 px-2 py-1.5 rounded-full text-xs">
                          {v.vendorCategory}
                        </span>
                      </td>

                      <td className="p-3">{v.mobile}</td>

                      <td className="p-3">{v.state}</td>

                      <td className="p-3">{v.city}</td>

                      <td className="p-3">
                        {v.account_number
                          ? `••••${v.account_number.slice(-4)}`
                          : "-"}
                      </td>

                      <td className="p-3">
                        <span className="bg-red-100 text-red-600 px-2 py-1 text-xs rounded-full">
                          Inactive
                        </span>
                      </td>

                      <td className="p-3 text-xs">
  {formatDateTime(v.createdOn, true)}
</td>

                      <td className="p-3">
                        <button
                          onClick={() => {
                            setSelectedVendor(v);
                            resetPurchaseForm();
                            setShowVendorPurchase(true);
                          }}
                          className="flex items-center gap-1 bg-green-600 text-white px-3 py-2 rounded-lg text-sm"
                        >
                          <Plus size={14} />
                          Add
                        </button>
                      </td>

                      <td className="p-3 text-right">
                        <button
                          onClick={() => openEditVendor(v)}
                          className="p-2 border rounded-lg"
                        >
                          <Pencil size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/*  PURCHASE TABLE */}

        {activeTab === "purchases" && (
          <div className="bg-white border rounded-xl overflow-auto h-full overflow-x-auto montserrat-font-1">
            <table className="w-full text-sm montserrat-font-1">
              <thead className="sticky top-0 z-10 bg-green-100 text-gray-600">
                <tr>
                  <th className="p-3 text-left">Purchase ID</th>
                  <th className="p-3 text-left">Vendor ID</th>
                  <th className="p-3 text-left">Vendor</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-left">Amount</th>
                  <th className="p-3 text-left">Purchase Date</th>
                  <th className="p-3 text-left">Paid</th>
                  <th className="p-3 text-left">Mode</th>
                  <th className="p-3 text-left">Account #</th>
                  <th className="p-3 text-left">IFSC</th>
                  <th className="p-3 text-left">Created On</th>
                  <th className="p-3 text-left">Pay</th>
                </tr>
              </thead>

              <tbody>
                {filteredPurchases.map((p) => (
                  <tr key={p.id} className="border-t hover:bg-gray-50">
                    <td className="p-3">{p.id || "N/A"}</td>
                    <td className="p-3">{p.fk_vendor_id || "N/A"}</td>
                    <td className="p-3">{p.vendor_name || "N/A"}</td>
                    <td className="p-3">{p.purchase_description || "N/A"}</td>

                    <td className="p-3 font-medium">₹ {p.amount || "N/A"}</td>

             <td className="p-3 text-xs">
  {formatDate(p.purchase_date)}
</td>

                    <td className="p-3">
                      {p.paid === "Y" ? (
                        <span className="bg-green-100 text-green-600 px-2 py-1 text-xs rounded-full">
                          Paid
                        </span>
                      ) : (
                        <span className="bg-red-100 text-red-600 px-2 py-1 text-xs rounded-full">
                          Unpaid
                        </span>
                      )}
                    </td>

                    <td className="p-3">{p.mode}</td>

                    <td className="p-3">
                      {p.account_number
                        ? `••••${p.account_number.slice(-4)}`
                        : "-"}
                    </td>

                    <td className="p-3">{p.ifsc_code || "N/A"}</td>

                    <td className="p-3 text-xs">{p.created_on || "N/A"}</td>
                    <td className="p-3 flex gap-2">
                      <button
                        onClick={() => openPayModal(p, "imps")}
                        disabled={p.paid === "Y"}
                        className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs
      ${
        p.paid === "Y"
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "bg-green-600 text-white"
      }`}
                      >
                        <IndianRupee size={12} />
                        Pay (IMPS)
                      </button>

                      <button
                        onClick={() => openPayModal(p, "cash")}
                        disabled={p.paid === "Y"}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs
      ${
        p.paid === "Y"
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "bg-blue-500 text-white"
      }`}
                      >
                        ₹ Pay Cash
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <OrderVendorsAction
          showAddVendor={showAddVendor}
          closeAddVendor={closeAddVendor}
          openCategory={openCategory}
          setOpenCategory={setOpenCategory}
          form={form}
          setForm={setForm}
          handleAddVendor={handleAddVendor}
          VENDOR_TYPES={VENDOR_TYPES}
          PRODUCT_CATEGORIES={PRODUCT_CATEGORIES}
          showAddPurchase={showAddPurchase}
          closeAddPurchase={closeAddPurchase}
          vendorNameInput={vendorNameInput}
          setVendorNameInput={setVendorNameInput}
          amount={amount}
          setAmount={setAmount}
          desc={desc}
          setDesc={setDesc}
          date={date}
          setDate={setDate}
          paymentMode={paymentMode}
          setPaymentMode={setPaymentMode}
          handleAddPurchase={handleAddPurchase}
          vendors={vendors}
          selectedVendor={selectedVendor}
          setSelectedVendor={setSelectedVendor}
          showVendorPurchase={showVendorPurchase}
          closeVendorPurchase={closeVendorPurchase}
          handleVendorPurchase={handleVendorPurchase}
          showEditVendor={showEditVendor}
          closeEditVendor={closeEditVendor}
          handleEditVendor={handleEditVendor}
          showPayModal={showPayModal}
          selectedPurchase={selectedPurchase}
          closePayModal={closePayModal}
          handleConfirmPayment={handleConfirmPayment}
        />
      </div>
    </>
  );
}
